// rules.jsx — story + rules screens (reference UI #2, #3, #13; old/rules1-4.php)
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './rules.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectAdminToken } from '../sessionSlice';
import { setBackButtonUrl } from '../uiSlice';
import { resolveGameEntryRoute } from '../../functions/stageReportSync';
import { Parchment, PirateButton, useDisableInspect, useSceneBackground } from '../escapeShared';
import bg2 from '../img/bg2.jpg';
import bg3 from '../img/bg3.jpg';
import bg5 from '../img/bg5.jpg';
import bg6 from '../img/bg6.jpg';
import mobBg2 from '../img/mob_bg2.jpg';
import mobBg3 from '../img/mob_3.jpg';
import mobBg4 from '../img/mob_bg4.jpg';

// Scene per step: [desktop, mobile]
const STEP_SCENES = [
  [bg2, mobBg2], // mission brief
  [bg3, mobBg3], // island
  [bg6, mobBg4], // locked in
  [bg5, mobBg2], // rules
];
const TOTAL_STEPS = STEP_SCENES.length;

// old/rules4.php's .rule-list
const RULE_ITEMS = [
  'You are trapped in a room that has a countdown starting at 20mins',
  'You need to navigate through items in your room that will lead you to find your way out',
  'Think smart. Think fast. Think together',
  'May the Smartest agents win!',
  'On inserting the correct answer, text box will turn green',
];

/** Redux's auth.user is only ever populated by Login's own mount effect, so a
 *  hard refresh (or deep link) straight into /rules leaves it null forever —
 *  fall back to the same sessionStorage userData thankyou.jsx already reads. */
function readSessionUser() {
  try {
    const raw = JSON.parse(sessionStorage.getItem("userData") || "{}");
    if (raw && typeof raw === "object" && (raw.userId || raw.userid || raw.id)) {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: reduxUser } = useSelector((state) => state.auth);
  const user = useMemo(() => reduxUser || readSessionUser(), [reduxUser]);
  const adminToken = useSelector(selectAdminToken);
  const [nextLoading, setNextLoading] = useState(false);
  const [step, setStep] = useState(1);

  useDisableInspect();
  useSceneBackground(...STEP_SCENES[step - 1]);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [dispatch]);

  const goToGame = useCallback(async () => {
    if (!user || nextLoading) return;

    let storedUser = user;
    try {
      storedUser = JSON.parse(sessionStorage.getItem("userData") || "{}");
    } catch {
      storedUser = user;
    }

    setNextLoading(true);
    try {
      const route = await resolveGameEntryRoute({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass: storedUser.sessionId === "demobypass",
      });
      navigate(route, { replace: true });
    } catch (err) {
      console.error("rules goToGame:", err);
      navigate("/game", { replace: true });
    } finally {
      setNextLoading(false);
    }
  }, [user, nextLoading, backendBase, adminToken, navigate]);

  const handleNext = (e) => {
    e.preventDefault();
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
      window.scrollTo(0, 0);
      return;
    }
    goToGame();
  };

  return (
    <div className={`escape-rules escape-rules--step${step}`} key={step}>
      {step === 1 && (
        <Parchment variant="scroll" className="escape-rules__card">
          <p>
            You had been assigned the mission to go on a hunt for the Queen's jewels that had been
            stolen by the <strong className="escape-rules__red">BLACK BEARD</strong> Pirates.
          </p>
          <p>
            Working as Undercover Agents, you sailed to the Exotica Pirate Island, where you adorned
            the avatar of Pirates to locate the jewels and return them to the Queen.
          </p>
          <p className="escape-rules__blue">
            There were other troupes that had been given the same Mission but they seem to be
            unreachable. Could the Pirates have gotten the better of them? Well, there is only one
            way to tell.
          </p>
          <PirateButton onClick={handleNext}>Next</PirateButton>
        </Parchment>
      )}

      {step === 2 && (
        <Parchment className="escape-rules__card">
          <h3>Now you are at the Island looking for the Jewels.</h3>
          <p>
            You have navigated through the mysterious land and reached upon a room that seems to be
            a holding space.
          </p>
          <p>Could this be a trap? Or could this spot have the jewels you're looking for?</p>
          <p>
            You and your crew decide to step inside the room and continue your search. And to your
            pleasant surprise you find the treasure chest!
          </p>
          <PirateButton onClick={handleNext}>Next</PirateButton>
        </Parchment>
      )}

      {step === 3 && (
        <Parchment className="escape-rules__card">
          <h3>Alassss! You hear a loud noise... What is that?</h3>
          <p>You turn around to see the door lock behind you. Were the Pirates watching?</p>
          <p>You hear a noise that sounds like a ticking clock...</p>
          <p>A digital device shows a countdown of 20 mins... And now it's 19:59 mins... Christ!</p>
          <p className="escape-rules__red">
            <strong>You're locked and you need to find a way to escape...</strong>
          </p>
          <p>Accept the challenge and plan your escape before you are in lockdown forever!</p>
          <PirateButton className="escape-rules__wide-btn" onClick={handleNext}>
            Accept Challenge
          </PirateButton>
        </Parchment>
      )}

      {step === 4 && (
        <div className="escape-rules__rules">
          <h2 className="escape-rules__ribbon">
            <span className="escape-rules__ribbon-band">Rules</span>
          </h2>
          <Parchment variant="scroll" className="escape-rules__card escape-rules__card--rules">
            <ul className="escape-rules__list">
              {RULE_ITEMS.map((text) => (
                <li key={text}>
                  <span className="escape-rules__skull" aria-hidden="true">☠</span>
                  {text}
                </li>
              ))}
            </ul>
            <PirateButton onClick={handleNext} disabled={nextLoading}>
              {nextLoading ? "Loading…" : "Next"}
            </PirateButton>
          </Parchment>
        </div>
      )}
    </div>
  );
}

export default UserRules;
